import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Calendar, Wallet, Settings, ListChecks, LogOut, Layers, BarChart3, Trophy, MessageCircle, Menu, X, Dices } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import AdminInstallBanner from "../AdminInstallBanner";

const ITEMS = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
  { to: "/admin/markets", icon: Layers, label: "Markets" },
  { to: "/admin/results", icon: Calendar, label: "Results" },
  { to: "/admin/jantri", icon: BarChart3, label: "Jantri Report" },
  { to: "/admin/casino", icon: Dices, label: "Casino History" },
  { to: "/admin/users", icon: Users, label: "Users" },
  { to: "/admin/chat", icon: MessageCircle, label: "Live Chat", badge: true },
  { to: "/admin/payments", icon: Wallet, label: "Payments" },
  { to: "/admin/bids", icon: ListChecks, label: "All Bids" },
  { to: "/admin/winners", icon: Trophy, label: "Winners" },
  { to: "/admin/settings", icon: Settings, label: "Settings" },
];

export default function AdminLayout({ children }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Native mobile-friendly viewport for admin — no forced desktop zoom.
  // Every admin page now has responsive card layouts, no horizontal scroll needed.
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    const prev = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "width=device-width, initial-scale=1, viewport-fit=cover");
    return () => {
      if (meta) meta.setAttribute("content", prev || "width=device-width, initial-scale=1");
    };
  }, []);

  useEffect(() => {
    const check = () => api.get("/admin/chat/unread").then(({ data }) => setUnread(data.count || 0)).catch(() => {});
    check();
    const id = setInterval(check, 6000);
    return () => clearInterval(id);
  }, []);

  // Close drawer whenever route changes (mobile UX)
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  const NavList = ({ onNav }) => (
    <nav className="flex-1 py-2 overflow-y-auto">
      {ITEMS.map(({ to, icon: Icon, label, exact, badge }) => {
        const active = exact ? loc.pathname === to : loc.pathname === to || loc.pathname.startsWith(to + "/");
        return (
          <Link
            key={to}
            to={to}
            onClick={onNav}
            data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`relative flex items-center gap-3 px-5 py-3 text-sm transition ${
              active ? "bg-orange-50 text-[#FF7A00] border-r-2 border-[#FF7A00] font-medium" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1">{label}</span>
            {badge && unread > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-black flex items-center justify-center animate-pulse" data-testid="admin-chat-badge">
                {unread}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex" data-admin-route>
      {/* ============== Desktop sidebar ============== */}
      <aside className="hidden md:flex w-60 lg:w-64 bg-white border-r border-slate-200 flex-col fixed inset-y-0 left-0 z-30">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="font-display font-bold text-lg tracking-tight text-slate-900">REDDY <span className="text-[#FF7A00]">CLUB</span></div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">Admin Console</div>
        </div>
        <NavList />
        <div className="border-t border-slate-200 p-4">
          <button
            data-testid="admin-logout-btn"
            onClick={() => { logout(); navigate("/admin/login"); }}
            className="w-full flex items-center justify-center gap-2 text-sm border border-slate-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200 py-2 rounded-lg transition"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* ============== Mobile top bar (hidden on /admin/chat for full-screen chat UX) ============== */}
      <header className={`md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-slate-200 shadow-sm ${loc.pathname.startsWith("/admin/chat") ? "hidden" : ""}`}>
        <div className="flex items-center gap-2 px-3 py-2.5">
          <button
            data-testid="admin-mobile-menu-btn"
            onClick={() => setDrawerOpen(true)}
            className="p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5 text-slate-700" />
          </button>
          <div className="flex-1">
            <div className="font-display font-bold text-base leading-tight text-slate-900">
              REDDY <span className="text-[#FF7A00]">CLUB</span>
            </div>
            <div className="text-[9px] uppercase tracking-widest text-slate-400 leading-none">Admin</div>
          </div>
          {unread > 0 && (
            <Link to="/admin/chat" className="relative p-2 rounded-lg hover:bg-slate-100">
              <MessageCircle className="w-5 h-5 text-slate-700" />
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-emerald-500 text-white text-[9px] font-black flex items-center justify-center">
                {unread}
              </span>
            </Link>
          )}
        </div>
      </header>

      {/* ============== Mobile drawer ============== */}
      {drawerOpen && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setDrawerOpen(false)} />
          <aside className="md:hidden fixed inset-y-0 left-0 w-72 max-w-[80%] bg-white z-50 flex flex-col shadow-2xl animate-in slide-in-from-left">
            <div className="px-4 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-lg tracking-tight text-slate-900">REDDY <span className="text-[#FF7A00]">CLUB</span></div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Admin Console</div>
              </div>
              <button onClick={() => setDrawerOpen(false)} className="p-2 rounded-lg hover:bg-slate-100" aria-label="Close menu">
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            <NavList onNav={() => setDrawerOpen(false)} />
            <div className="border-t border-slate-200 p-4">
              <button
                onClick={() => { logout(); navigate("/admin/login"); }}
                className="w-full flex items-center justify-center gap-2 text-sm border border-slate-200 hover:bg-red-50 hover:text-red-600 py-2 rounded-lg transition"
              >
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </aside>
        </>
      )}

      {/* ============== Main content ============== */}
      <main className={`flex-1 md:ml-60 lg:ml-64 overflow-x-hidden min-w-0 w-full ${loc.pathname.startsWith("/admin/chat") ? "pt-0" : "pt-14 md:pt-0"}`}>
        <div className={`max-w-full md:max-w-7xl mx-auto ${loc.pathname.startsWith("/admin/chat") ? "" : "px-2 sm:px-4 md:px-6 lg:px-8 py-3 md:py-8"}`} data-testid="admin-content">
          {children}
        </div>
      </main>

      {/* PWA install prompt (shows on capable browsers when app not yet installed) */}
      <AdminInstallBanner />
    </div>
  );
}
